import React, { useState, useMemo } from "react";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import {
	GameTeamSheetGameTeamPlayersSearchResponse,
	GPGamePlayerResponse, GPGameTeamResponse,
	GSTGameTeamPlayerResponse
} from "../../../types";
import { _ } from "i18n";
import "./PlayerTable.css";
import clsx from "clsx";

interface PlayerTableProps {
	players: GPGamePlayerResponse[];
	positionsOptions: { value: string | number; label: string }[];
	clubPlayerOptions: { value: string | number; label: string }[];
	handlePlayerChange: (index: number, field: keyof GPGamePlayerResponse, value: any) => void;
	handlePlayerSelect: (index: number, selectedOption: any) => void;
	handleRemovePlayer: (index: number, isSubstitute: boolean) => void;
	searchPlayers: (teamId: number, filter: string) => Promise<GameTeamSheetGameTeamPlayersSearchResponse | null>;
	selectedTeam: GPGameTeamResponse;
	isLoadingClubPlayers: boolean;
	canEditNumbers: boolean;
	canRemovePlayers: boolean;
	canAnonymizePlayers: boolean;
	supportsPlainTeamSheet: boolean;
}

const PlayerTable: React.FC<PlayerTableProps> = ({
	players,
	positionsOptions,
	clubPlayerOptions,
	handlePlayerChange,
	handlePlayerSelect,
	handleRemovePlayer,
	searchPlayers,
	selectedTeam,
	isLoadingClubPlayers,
	canEditNumbers,
	canRemovePlayers,
	canAnonymizePlayers,
	supportsPlainTeamSheet,
}) => {
	const [searchResults, setSearchResults] = useState<{ [key: number]: GameTeamSheetGameTeamPlayersSearchResponse | null}>({});
	const [anonymizedTooltipShown, setAnonymizedTooltipShown] = useState<boolean>(false);
	const selectedPlayerIds = useMemo(() => new Set(players.map(p => p.playerId).filter(id => id)), [players]);

	const filteredClubPlayerOptions = useMemo(() =>
		clubPlayerOptions.map(option => ({
			...option,
			isDisabled: selectedPlayerIds.has(Number(option.value))
		})),
		[clubPlayerOptions, selectedPlayerIds]
	);

	const handleSearch = async (index: number, value: string) => {
		handlePlayerChange(index, "name", value);

		if (value.length >= 3) {
			const results = await searchPlayers(selectedTeam.id, value);

			setSearchResults((prev) => ({ ...prev, [index]: results }));
		} else {
			setSearchResults((prev) => ({ ...prev, [index]: null }));
		}
	};

	const handleSelectPlayer = (index: number, player: GSTGameTeamPlayerResponse) => {
		handlePlayerChange(index, "name", player.name);
		handlePlayerChange(index, "playerId", player.id);
		setSearchResults((prev) => ({ ...prev, [index]: null }));
	};

	return (
		<table className="team-sheet-modal__player-table">
			<thead>
			<tr>
				<th>{_("matchSheet.table.position")}</th>
				<th>{_("matchSheet.table.playerName")}</th>
				<th>{_("matchSheet.table.numberSign")}</th>
				<th>{_("matchSheet.table.goal")}</th>
				{canAnonymizePlayers && <th className="team-sheet-modal__player-table__header" onMouseEnter={() => setAnonymizedTooltipShown(true)} onMouseLeave={() => setAnonymizedTooltipShown(false)}>
					{_("matchSheet.table.anonymized")}
					<span className="icon-info-outline icon-new icon-white icon-16 info-icon"></span>
					{anonymizedTooltipShown && <div className="tooltip">
						<strong>{_("matchSheet.table.anonymized.tooltip.title")}</strong>
						<span>{_("matchSheet.table.anonymized.tooltip.content")}</span>
					</div>}
				</th>}
				{canRemovePlayers &&
					<th></th>
				}
			</tr>
			</thead>
			<tbody>
			{players.map((player, index) => (
				<tr key={player.id || `player-${index}`}>
					<td className="team-sheet-modal__player-table__col team-sheet-modal__player-table__col-med">
						<div className="selectContainer">
							<SelectComponent
								value={positionsOptions.find((option) => option.value === player.position) || { value: "", label: "-" }}
								handleChange={(selectedOption) => handlePlayerChange(index, "position", selectedOption?.value || "")}
								items={positionsOptions}
								isCleareable={false}
							/>
						</div>
					</td>
					<td className="team-sheet-modal__player-table__col team-sheet-modal__player-table__col-large team-sheet-modal__player-table__col-name">
						{(selectedTeam.clubPlayersCount > 0 && player.playerId !== null) || !supportsPlainTeamSheet ? (
							<div className="selectContainer">
								<SelectComponent
									value={filteredClubPlayerOptions.find((option) => option.value === player.playerId) || null}
									handleChange={(selectedOption) => handlePlayerSelect(index, selectedOption)}
									items={filteredClubPlayerOptions}
									isCleareable={false}
									isLoading={isLoadingClubPlayers}
								/>
							</div>
						) : (
							<div className="autocomplete-container">
								<input
									type="text"
									value={player.name || ""}
									onChange={(e) => handleSearch(index, e.target.value)}
									className="autocomplete-input"
								/>
								{searchResults[index]?.players?.length ? (
									<ul className="autocomplete-list">
										{searchResults[index]?.players.map((result) => (
											<li key={result.id} onClick={() => handleSelectPlayer(index, result)}>
												{result.name}
											</li>
										))}
									</ul>
								) : null}
							</div>
						)}
					</td>
					<td className="team-sheet-modal__player-table__col team-sheet-modal__player-table__col-small">
						<input
							readOnly={player.id != null && !canEditNumbers}
							type="text"
							value={player.numberSign ?? ""}
							onChange={(e) => {
								const raw = e.target.value;
								handlePlayerChange(index, "numberSign", raw === "" ? null : Number(raw));
							}}
							title={player.id && !canEditNumbers ? _("matchSheet.table.numberSign.readonly.tooltip") : ""}
							className={player.id && !canEditNumbers ? "readonly-input" : ""}
						/>
					</td>
					<td className="team-sheet-modal__player-table__col team-sheet-modal__player-table__col-small">
						<input
							type="text"
							value={player.goals || ""}
							onChange={(e) => handlePlayerChange(index, "goals", Number(e.target.value))}
						/>
					</td>
					{canAnonymizePlayers && <td className="team-sheet-modal__player-table__col team-sheet-modal__player-table__col-small">
						<span
							className={clsx("anonymized", {
								"is-active": player.isAnonymized,
							})}
							onClick={() => handlePlayerChange(index, "isAnonymized", !player.isAnonymized)}
						>
							<span className="icon-new icon-18 icon icon-blue icon-eye-plain icon-eye" />
						</span>
					</td>}
					{canRemovePlayers &&
						<td className="team-sheet-modal__player-table__col team-sheet-modal__player-table__col-actions">
							{player.isSubstitute ? (
								<a title={_("btn.delete")} href="javascript:void(0);" id="deleteRow" onClick={() => handleRemovePlayer(index, true)}>
									<span className="icon-new icon-18 icon icon-blue icon-bin" />
								</a>
							) : (
								<a title={_("btn.delete")} href="javascript:void(0);" id="deleteRow" onClick={() => handleRemovePlayer(index, false)}>
									<span className="icon-new icon-18 icon icon-blue icon-cancel-circle" />
								</a>
							)}
						</td>
					}
				</tr>
			))}
			</tbody>
		</table>
	);
};

export default PlayerTable;
